import { useEffect, useState } from 'react'
import { Badge, Button, Group, Popover, Text } from '@mantine/core'
import IonIcon from '@reacticons/ionicons'
import { useForm } from '@mantine/form'
import { NewSelectInput } from 'app/views/components/inputs/selects/NewSelectInput'
import {
  FacilityHistoryFilterValues,
  saveFacilityHistoryFiltersToStorage,
} from 'app/utils/grid-sorting-storage'
import { toTitleCase } from 'app/utils/util-functions'

const LEVEL_OPTIONS = ['Facility', 'Loan']

const TRANSACTION_TYPE_OPTIONS = [
  { value: 'FacilityCreation', label: 'Facility Creation' },
  { value: 'FacilityEdit', label: 'Facility Edit' },
  { value: 'FacilityAmendment', label: 'Facility Amendment' },
  { value: 'FacilityLenderAmendment', label: 'Facility Lender Amendment' },
  {
    value: 'FacilityUtilizedAmountChanged',
    label: 'Facility Utilized Amount Changed',
  },
  { value: 'FacilityTermination', label: 'Facility Termination' },
  { value: 'LoanCreation', label: 'Loan Creation' },
  { value: 'LoanAmendment', label: 'Loan Amendment' },
  { value: 'LoanLenderAllocation', label: 'Loan Lender Allocation' },
  { value: 'LoanEdit', label: 'Loan Edit' },
  { value: 'LoanIncrease', label: 'Loan Increase' },
  { value: 'LoanMerge', label: 'Loan Merge' },
  { value: 'LoanConversion', label: 'Loan Conversion' },
  { value: 'LoanRollover', label: 'Loan Rollover' },
  { value: 'LoanPik', label: 'Loan PIK' },
  { value: 'LoanPrepayment', label: 'Loan Prepayment' },
  { value: 'LoanInterestPayment', label: 'Loan Interest Payment' },
  { value: 'LocBooked', label: 'LoC Booked' },
  { value: 'Trade', label: 'Trade' },
  { value: 'Assignment', label: 'Assignment' },
  { value: 'ReprocessBlocker', label: 'Processing Blocker' },
]

const SOURCE_OPTIONS = [
  { value: 'fcl', label: 'FCL' },
  { value: 'facilityHistory', label: 'Facility History' },
]

type Props = {
  filterValues: FacilityHistoryFilterValues
  setFilterValues: (values: FacilityHistoryFilterValues) => void
}

const LABEL_OVERRIDES: Record<string, string> = {
  transactionType: 'Transaction Type',
  source: 'Source',
  level: 'Level',
}

function badgeLabel(key: string, value: string): string {
  if (key === 'transactionType') {
    return (
      TRANSACTION_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value
    )
  }
  if (key === 'source') {
    return SOURCE_OPTIONS.find((o) => o.value === value)?.label ?? value
  }
  return value
}

export default function FacilityHistoryFilter({
  filterValues,
  setFilterValues,
}: Props) {
  const [isPopoverOpen, setPopoverOpen] = useState(false)
  const togglePopover = () => setPopoverOpen((prev) => !prev)

  const form = useForm<FacilityHistoryFilterValues>({
    initialValues: filterValues,
  })

  useEffect(() => {
    form.setValues(filterValues)
  }, [filterValues])

  const submitForm = (event: { preventDefault: () => void }) => {
    event.preventDefault()
    setFilterValues(form.values)
    saveFacilityHistoryFiltersToStorage(form.values)
    setPopoverOpen(false)
  }

  const removeFilter = (key: keyof FacilityHistoryFilterValues) => {
    const updated = { ...filterValues, [key]: '' }
    setFilterValues(updated)
    saveFacilityHistoryFiltersToStorage(updated)
  }

  const activeEntries = Object.entries(filterValues).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  ) as Array<[keyof FacilityHistoryFilterValues, string]>

  return (
    <div className="container">
      {activeEntries.map(([key, value]) => (
        <Badge
          key={key}
          classNames={{ root: 'customBadge' }}
          size="lg"
          color="blue"
          rightSection={
            <IonIcon
              name="close-outline"
              style={{
                cursor: 'pointer',
                color: '#784595',
                width: '1.2em',
                height: '1.2em',
              }}
              onClick={() => removeFilter(key)}
            />
          }
        >
          <Group>
            <Text className="customBadgeText">
              {`${LABEL_OVERRIDES[key] ?? toTitleCase(key)}: `}
            </Text>
            <Text className="customBadgeTextValue">{badgeLabel(key, value)}</Text>
          </Group>
        </Badge>
      ))}
      <Popover
        opened={isPopoverOpen}
        onClose={() => setPopoverOpen(false)}
        position="bottom"
        shadow="md"
        middlewares={{ flip: true, shift: true, inline: true }}
      >
        <Popover.Target>
          <Button
            variant="outline"
            size="md"
            className={`plusButton ${isPopoverOpen ? 'plusButtonOpen' : 'plusButtonClosed'}`}
            onClick={togglePopover}
          >
            <IonIcon
              name="add-outline"
              style={{
                width: '2em',
                height: '2em',
                color: isPopoverOpen ? '#FFFFFF' : '#32439A',
                fontWeight: 800,
              }}
            />
          </Button>
        </Popover.Target>
        <Popover.Dropdown style={{ borderRadius: 12 }}>
          <form onSubmit={submitForm}>
            <div className="filterMenu">
              <div className="filterMenuSelected">
                <div className="filterMenuLeft">
                  <NewSelectInput
                    wrapperType="trade"
                    label="Level"
                    data={LEVEL_OPTIONS}
                    w="200px"
                    h="42px"
                    placeholder="Level"
                    clearable
                    {...form.getInputProps('level')}
                  />
                  <NewSelectInput
                    wrapperType="trade"
                    label="Transaction Type"
                    data={TRANSACTION_TYPE_OPTIONS}
                    w="240px"
                    h="42px"
                    placeholder="Transaction Type"
                    clearable
                    searchable
                    {...form.getInputProps('transactionType')}
                  />
                  <NewSelectInput
                    wrapperType="trade"
                    label="Source"
                    data={SOURCE_OPTIONS}
                    w="200px"
                    h="42px"
                    placeholder="Source"
                    clearable
                    {...form.getInputProps('source')}
                  />
                </div>
              </div>
              <Button className="applyButton" type="submit">
                Apply
              </Button>
            </div>
          </form>
        </Popover.Dropdown>
      </Popover>
    </div>
  )
}
